// plugins/prometheus/src/api/AiForecastApi.ts
import {
  ConfigApi,
  createApiRef,
  FetchApi,
} from '@backstage/core-plugin-api';

export const aiForecastApiRef = createApiRef<AiForecastAPI>({
  id: 'plugin.ai-forecast.service',
});

// Constants
const DEFAULT_FORECAST_HOURS = 168; // 7 days
const DEFAULT_DATA_ID = 'prometheus_data';
const DEFAULT_USER_ID = 'backstage_user';
const MAX_FORECAST_HOURS = 720; // 30 days
const MIN_FORECAST_HOURS = 1;
const MIN_DATA_POINTS = 10; // Require more than 10 points to use actual data

// CSV Headers
const CSV_HEADERS = [
  'timestamp',
  'cpu_utilization',
  'memory_usage',
  'network_in',
  'network_out',
  'network_packets_in',
  'network_packets_out',
  'disk_read_write',
  'disk_read',
  'disk_write',
  'event',
] as const;

export interface ForecastMetric {
  predicted: number;
  lower_bound: number;
  upper_bound: number;
}

export interface ForecastPrediction {
  timestamp: string;
  event: string;
  metrics: {
    [metricName: string]: ForecastMetric;
  };
}

export interface ForecastResponse {
  id: string;
  content: string;
  provider: string;
  predictions_count: number;
  raw_predictions: ForecastPrediction[];
}

export interface ForecastOptions {
  forecastHours?: number;
  dataId?: string;
  userId?: string;
  company?: string;
}

type Options = {
  configApi: ConfigApi;
  fetchApi: FetchApi;
};

export class AiForecastAPI {
  private readonly aiAdapterUrl: string;
  private readonly fetchApi: FetchApi;
  private readonly defaultForecastHours: number;
  private readonly defaultDataId: string;
  private readonly defaultUserId: string;

  constructor(options: Options) {
    const backendUrl = options.configApi.getOptionalString('backend.baseUrl');
    this.aiAdapterUrl = `${backendUrl}/api/ai-adapter`;
    this.fetchApi = options.fetchApi;
    
    // Read configuration with defaults
    this.defaultForecastHours = options.configApi.getOptionalNumber(
      'aiForecast.defaultForecastHours'
    ) ?? DEFAULT_FORECAST_HOURS;
    
    this.defaultDataId = options.configApi.getOptionalString(
      'aiForecast.defaultDataId'
    ) ?? DEFAULT_DATA_ID;
    
    this.defaultUserId = options.configApi.getOptionalString(
      'aiForecast.defaultUserId'
    ) ?? DEFAULT_USER_ID;
  }

  async generateForecast(
    csvData: string,
    metrics: string[],
    options: ForecastOptions = {}
  ): Promise<ForecastResponse> {
    const forecastHours = options.forecastHours ?? this.defaultForecastHours;
    const dataId = options.dataId ?? this.defaultDataId;
    const userId = options.userId ?? this.defaultUserId;

    // Validate forecast hours
    if (forecastHours < MIN_FORECAST_HOURS || forecastHours > MAX_FORECAST_HOURS) {
      throw new Error(
        `Forecast hours must be between ${MIN_FORECAST_HOURS} and ${MAX_FORECAST_HOURS}`
      );
    }

    const apiUrl = `${this.aiAdapterUrl}/proxy/v1/resource_forecast/forecast`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const formData = new FormData();

    formData.append('metrics', metrics.join(','));
    formData.append('forecast_hours', forecastHours.toString());
    formData.append('data_id', dataId);
    formData.append('user_id', userId);
    if (options.company) {
      formData.append('company', options.company);
    }
    formData.append('file', blob, 'resource_metrics.csv');

    const response = await this.fetchApi.fetch(apiUrl, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate forecast: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Convert Prometheus data to CSV format.
   * Uses actual data if more than MIN_DATA_POINTS data points are available.
   */
  convertPrometheusDataToCSV(data: any[]): string {
    const dataPoints = Array.isArray(data) ? data.length : 0;

    if (dataPoints > MIN_DATA_POINTS) {
      return this.convertActualDataToCSV(data);
    }

    throw new Error(
      `Insufficient Prometheus data points for forecasting. Provide more than ${MIN_DATA_POINTS} samples.`
    );
  }

  /**
   * Convert actual Prometheus data to CSV format
   */
  private convertActualDataToCSV(data: any[]): string {
    const rows = [CSV_HEADERS.join(',')];

    data.forEach(dataPoint => {
      // Handle timestamp conversion
      const timestamp = dataPoint.timestamp 
        ? new Date(dataPoint.timestamp * 1000).toISOString().slice(0, 19).replace('T', ' ')
        : new Date().toISOString().slice(0, 19).replace('T', ' ');

      // Build row with all metrics, using 0 as default for missing values
      const row = [
        timestamp,
        (dataPoint.cpu_utilization ?? 0).toFixed(2),
        (dataPoint.memory_usage ?? 0).toFixed(2),
        (dataPoint.network_in ?? 0).toFixed(2),
        (dataPoint.network_out ?? 0).toFixed(2),
        (dataPoint.network_packets_in ?? 0).toFixed(2),
        (dataPoint.network_packets_out ?? 0).toFixed(2),
        (dataPoint.disk_read_write ?? 0).toFixed(2),
        (dataPoint.disk_read ?? 0).toFixed(2),
        (dataPoint.disk_write ?? 0).toFixed(2),
        dataPoint.event ?? 'Normal Operation',
      ];

      rows.push(row.join(','));
    });

    return rows.join('\n');
  }
}
